import { DeliveriesEventArgs, DeliveriesStore, Delivery } from './DeliveriesStore';
import "./DeliveryControlElement.less";
import { CommentEditorElement } from '../Comments/CommentEditorElement';
import { CommentsStore, Comment } from '../Comments/CommentsStore';
import { DeliveryPictureElement } from './DeliveryPictureElement';
import { SignaturePadElement } from '../Signature/SignaturePadElement';
import { AttachmentStore } from '../Attachments/AttachmentStore';
import { DeliveryDetailsElement } from './DeliveryDetailsElement';
import { UrlProvider } from "../../Infrastructure/UrlProvider";

export class DeliveryControlElement extends HTMLElement {
    public static tag = "delivery-control";
    private _mimimizeButton: HTMLButtonElement;
    private _deliveredButton: HTMLButtonElement;
    private _undliverdButton: HTMLButtonElement;
    private _detailsArea: HTMLElement;
    private _commentArea: HTMLElement;

    connectedCallback() {
        const delivery = DeliveriesStore.instance.getDelivery(this.getDeliveryIdentifier(), this.getAttribute("route-name"));

        this.setAttribute("order-no", delivery.orderNo);



        this.innerHTML = this.view(delivery, this.mapComments(delivery));

        this._detailsArea = this.querySelector("[name=details]") as HTMLElement;

        this._deliveredButton = this.querySelector("button[name=delivered]");
        this._deliveredButton.addEventListener("click", this.onDelivered);
        this._deliveredButton.style.display = delivery.status !== "complete" ? "block" : "none";

        this._undliverdButton = this.querySelector("button[name=undeliver]");
        this._undliverdButton.addEventListener("click", this.onUndeliver);
        this._undliverdButton.style.display = delivery.status === "complete" ? "block" : "none";


        this._mimimizeButton = this.querySelector("button[name=minimize]");
        this._mimimizeButton.addEventListener("click", this.close);


        DeliveriesStore.instance.addEventListener("deliveries-updating", this.onDeliveriesUpdating);
        DeliveriesStore.instance.addEventListener("deliveries-updated", this.onDeliveriesUpdated);
        DeliveriesStore.instance.addEventListener("deliveries-updating-canceled", this.onDeliveriesUpdatedCanceled);

        this._commentArea = this.querySelector(".comments div");

        CommentsStore.instance.addEventListener("comment-added", this.onCommentAdded);

        

        (<any>window).deliveryControl = this;
    }

    disconnectedCallback() {
        this._mimimizeButton.removeEventListener("click", this.close);

        DeliveriesStore.instance.removeEventListener("deliveries-updating", this.onDeliveriesUpdating);
        DeliveriesStore.instance.removeEventListener("deliveries-updated", this.onDeliveriesUpdated);
        DeliveriesStore.instance.removeEventListener("deliveries-updating-canceled", this.onDeliveriesUpdatedCanceled);

        CommentsStore.instance.removeEventListener("comment-added", this.onCommentAdded);
    }

    private getDeliveryType() : "Invoice" | "Return" { 
        if (this.getAttribute("delivery-type") === "Invoice")
            return "Invoice";
        else if (this.getAttribute("delivery-type") === "Return")
            return "Return";
        else
            throw new Error(`Invalid delivery type: ${this.getAttribute("delivery-type")}`);
    }

    private getDeliveryIdentifier() { 
        return {
            type: this.getDeliveryType(),
            invoiceNo: this.getAttribute("invoice-id"),
            orderNo: this.getAttribute("order-id"),
        };
    }

    private mapComments(delivery: Delivery) {
        const comments = [...delivery.comments.map(m => m.text)];
        comments.reverse();
        return comments;
    }

    private onCommentAdded = (event: CustomEvent<Comment>) => {
        this._commentArea.insertAdjacentHTML("afterbegin", this.formatComment(event.detail.text));
    }

    private onUndeliver = async () => {
        await DeliveriesStore.instance.undeliver(this.getDeliveryIdentifier(), this.getAttribute("route-name"));
    }

    private onDelivered = async () => {
        await DeliveriesStore.instance.deliverAll(this.getDeliveryIdentifier(), this.getAttribute("route-name"));
    }

    private onDeliveriesUpdating = (event: CustomEvent<string>) => {
        if (this.hasAttribute("route-name")) { 
            if (event.detail !== this.getAttribute("route-name"))
                return;
        }

        this.toggleAttribute("busy", true);
        this._deliveredButton.disabled = true;
        this._undliverdButton.disabled = true;
    }

    private onDeliveriesUpdatedCanceled = (event: CustomEvent<void>) => { 
        this.toggleAttribute("busy", false);
    }

    private onDeliveriesUpdated = (event: CustomEvent<DeliveriesEventArgs>) => {
        if (this.hasAttribute("route-name")) { 
            if (event.detail.routeName !== this.getAttribute("route-name"))
                return;
        }

        
        const delivery = DeliveriesStore.instance.getDelivery(this.getDeliveryIdentifier(), event.detail.routeName);
        this._detailsArea.innerHTML = this.detailsView(delivery, this.mapComments(delivery));

        this._deliveredButton.disabled = false;
        this._undliverdButton.disabled = false;


        this._deliveredButton.style.display = delivery.status === "complete" ? "none" : "block";
        this._undliverdButton.style.display = delivery.status === "complete" ? "block" : "none";
        this.toggleAttribute("busy", false);
    }

    private close = (event: PointerEvent) => this.remove();

    private formatWeight = (weight: number) => {
        if (!weight)
            return "";

        return `${weight?.toString().replace(".", ",")} kg`;
    }

    private formatStatus = (status: "ongoing" | "complete") => {
        if (status === "ongoing")
            return "Not delivered";

        return "Delivered";
    }

    private pad(no: number, wantedLength = 2, symbol = "0") {
        const source = no.toString();
        return source.length >= wantedLength ? source : new Array(wantedLength - source.length + 1).join(symbol) + source;
    }

    private formatDate = (dateString: string) => {
        if (!dateString)
            return "";

        const date = new Date(`${dateString}Z`);
        return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${this.pad(date.getMinutes())}`;
    }

    private sanitize = (city: string) => {
        const forbidden = ["N", "S", "Ø", "V", "NØ", "NV", "SØ", "SV"];
        const parts = city.split(" ");

        const lastPart = parts[parts.length - 1];
        if (forbidden.includes(lastPart.toUpperCase()))
            parts.pop();

        return parts.join(" ");
    };

    private buildGoogleUrl(delivery: Delivery) {
        const countryArea = this.getCountryArea(delivery);
        var search = new URLSearchParams();

        search.append("api", "1");
        search.append("destination", `${delivery.deliveryAddress}, ${this.sanitize(delivery.deliveryCity)}${countryArea !== "" ? `, ${countryArea}, Denmark` : ""}`);
        search.append("travelmode", "driving");
        search.append("dir_action", "navigate");

        return `https://www.google.com/maps/dir/?${search.toString()}`;
    }

    private getCountryArea = (delivery: Delivery) => { 
        const zipCode = parseInt(delivery.deliveryZipCode.toString());

        if (isNaN(zipCode))
            return "";

        if (zipCode < 1000)
            return "";
        
        if (zipCode < 5000)
            return "Zealand";

        if (zipCode < 6000)
            return "Fyn";
        
        return "Jutland";
    }

    private getDeliveryMessage = (delivery: Delivery) => `${delivery.deliveryMessage} ${delivery.stockMessage}`.trim();

    private detailsView = (delivery: Delivery, comments: string[]) => `
   
        <div name=delivery-status>
            ${delivery.status === "complete" ? require("./Images/complete-delivery.svg") : require("./Images/ongoing-delivery.svg")}
        </div>

        <div class=line-form>
            <label>Invoice number</label>
            <div class=invoice-id>${delivery.invoiceNo}</div>
        </div>

        <div class=line-form>
            <label>Order number</label>
            <div class=invoice-id>${delivery.orderNo}</div>
        </div>

       

        <div class="line-form double">
            <div>
                <label>Priority</label>
                <div class=invoice-id>${delivery.priority}</div>
            </div>
            <div>
                <label>Weight</label>
                <div class=invoice-id>${this.formatWeight(delivery.weight)}</div>
            </div>
        </div>

        <div class="line-form address">
            <label>Address</label>
            <div>
                <a
                target=_blank
                href="${this.buildGoogleUrl(delivery)}">
                    ${delivery.deliveryName}<br />
                    <span>
                        ${delivery.deliveryAddress}<br />
                        ${(delivery.deliveryAddress2 && delivery.deliveryAddress2 != "") ? `${delivery.deliveryAddress2}<br />` : ""}
                        ${delivery.deliveryZipCode} ${delivery.deliveryCity}
                    </span>

                    <button>${require("../../Images/google-maps.svg")} Google Maps</button>
                </a>
            </div>
        </div>

        ${this.getDeliveryMessage(delivery) !== "" ? `
            <div class="line-form delivery-message">
                <label>Note</label>
                <div><span>${this.getDeliveryMessage(delivery)}</span></div>
            </div>
        ` : ""}

        <${DeliveryDetailsElement.tag} invoice-no="${delivery.invoiceNo}" order-id="${delivery.orderNo}" delivery-type="${delivery.type}"></${DeliveryDetailsElement.tag}>


        ${delivery.status === "complete" ? `
            <div class=line-form>
                <label>Delivered</label>
                <div>${this.formatDate(delivery.deliveredAt)} ${delivery.deliveredBy ? `(${delivery.deliveredBy})` : ""}</div>
            </div>
        `: ""}
        
        <div class="line-form comments">
            <label>
                Comments
            </label>
            <div>
                ${this.formatComments(comments || []).join("")}
            </div>
        </div>
    `

    private formatComments = (comments: string[]) => { 
        const result = [];
        for (const comment of comments) {
            result.push(this.formatComment(comment));
        }

        return result;
    }

    private formatComment = (comment: string) => { 
        const text = comment || "";

        const pictureMatch = text.match(/Picture (.*) added./);
        if (pictureMatch)
            return `<${DeliveryPictureElement.tag} 
            original-source="${UrlProvider.root}/api/attachments/${pictureMatch[1]}"
            source="${UrlProvider.root}/api/attachments/small/${pictureMatch[1]}"></${DeliveryPictureElement.tag}>`;
        else
           return `<text-comment>${text.replace(/(?:\r\n|\r|\n)/g, "<br>")}</text-comment>`;
    }

    private view = (delivery: Delivery, comments: string[]) => `
        <button class=icon name=minimize type=button>${require("./Images/close.svg")}</button>
        <header>${delivery.type === "Invoice" ? "Delivery" : "Return"}</header>

        <div name=details>
            ${this.detailsView(delivery, comments)}
        </div>

        <div class=actions>
            
            <button type=button name=delivered>${delivery.type === "Invoice" ? "Mark as delivered" : "Mark received"}</button>
            <button type=button name=undeliver>${delivery.type === "Invoice" ? "Undeliver" : "Mark NOT received"}</button>
            <${CommentEditorElement.tag} invoice-no="${delivery.invoiceNo}" order-no="${delivery.orderNo}" delivery-type="${delivery.type}"></${CommentEditorElement.tag}>
        </div>
    `;

}

customElements.define(DeliveryControlElement.tag, DeliveryControlElement);